/**
 * De qué color es una superficie, para decidir si el texto encima va claro.
 *
 * Es la regla del lienzo del editor —ver `applyAutoContrastBackground`—, traída
 * aquí para que la sigan también las páginas que se pintan fuera de él: la de
 * una oferta, el índice del catálogo y el cuerpo de un post. Sobre un fondo
 * oscuro la letra pasa a blanco; los filos y los textos apagados se derivan de
 * la letra, así que la siguen solos.
 *
 * ── El fallo que trajo esto al editor ───────────────────────────────────────
 * Sólo se entendían colores sólidos: un hex, un `rgb()` o un token del tema. Un
 * `linear-gradient(...)` no encajaba en ninguno, así que la función devolvía
 * «no sé» — y sin respuesta, la sección no publicaba ninguna señal de claro u
 * oscuro y el texto caía en el color por defecto del tema, que es oscuro.
 *
 * ── Qué se hace con un degradado ────────────────────────────────────────────
 * Se sacan sus paradas de color y se promedian. Un degradado no tiene un color,
 * tiene un recorrido, pero para esta decisión —¿el texto encima va blanco o
 * negro?— lo que importa es cómo se lee la superficie en conjunto. Un degradado
 * de blanco a negro no tiene respuesta buena; el promedio acierta en el caso
 * normal, el de degradados de una misma familia.
 */
import type { PageTheme } from './types'

export type Rgb = [number, number, number]

/** Lo que va encima de una superficie oscura: el mismo blanco que el editor. */
export const ON_DARK_TEXT = '#ffffff'

/** Un token del tema (`var(--wb-…)` o uno propio) resuelto a su valor. */
function resolveVar(value: string, theme?: PageTheme): string {
  const variable = value.match(/^var\((--[\w-]+)\)$/)?.[1]
  if (!variable || !theme) return value
  const component = 'colorComponents' in theme && Array.isArray(theme.colorComponents)
    ? (theme.colorComponents as Array<{ cssVar?: string; value?: string }>).find((item) => item.cssVar === variable)
    : undefined
  if (component?.value) return component.value
  if (variable.startsWith('--wb-')) {
    const key = variable.slice('--wb-'.length) as keyof PageTheme['colors']
    return theme.colors[key] ?? value
  }
  return value
}

function parseColor(value: string): Rgb | null {
  const trimmed = value.trim()
  const hex = trimmed.match(/^#([0-9a-f]{3}|[0-9a-f]{6})(?:[0-9a-f]{2})?$/i)?.[1]
  if (hex) {
    const full = hex.length === 3 ? [...hex].map((part) => part + part).join('') : hex
    return [0, 2, 4].map((index) => Number.parseInt(full.slice(index, index + 2), 16)) as Rgb
  }
  const rgb = trimmed.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i)
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null
}

/** Todo lo que parece un color dentro de un valor de CSS. */
function extractColors(value: string, theme?: PageTheme): Rgb[] {
  /* Se buscan las formas de color, no se parte por comas: dentro de un
     `rgb(0, 0, 0)` hay comas que no separan paradas, y partir ahí produciría
     tres trozos sin sentido. */
  const found = value.match(/#[0-9a-fA-F]{3,8}\b|rgba?\([^)]*\)|var\(--[\w-]+\)/g) ?? []
  return found
    .map((token) => parseColor(resolveVar(token, theme)))
    .filter((color): color is Rgb => color !== null)
}

/**
 * El color representativo de un fondo: el sólido, o la media del degradado.
 *
 * Devuelve `null` cuando no hay nada reconocible —una imagen, `none`, un valor
 * vacío—, y quien llama entiende ese `null` como «no puedo decidir», que es
 * distinto de decidir mal.
 */
export function resolvedSurfaceColor(value: string | undefined, theme?: PageTheme): Rgb | null {
  if (!value) return null
  const direct = parseColor(resolveVar(value.trim(), theme))
  if (direct) return direct
  const mixed = mixedColor(value.trim(), theme)
  if (mixed) return mixed

  const colors = extractColors(value, theme)
  if (colors.length === 0) return null
  const total = colors.reduce<Rgb>(
    (sum, color) => [sum[0] + color[0], sum[1] + color[1], sum[2] + color[2]],
    [0, 0, 0],
  )
  return total.map((channel) => Math.round(channel / colors.length)) as Rgb
}

/**
 * Un `color-mix(in srgb, A 35%, B)` con su proporción de verdad.
 *
 * Antes se promediaban sus colores a partes iguales: la portada de las listas
 * (`var(--wb-primary) 35%` sobre casi negro) con una marca lima se leía como
 * un tono medio «claro», y el título salía oscuro sobre fondo oscuro. Sin
 * porcentajes, mitad y mitad; con uno, el otro es lo que falta hasta 100. Lo
 * que se mezcla con `transparent` no es un color sólido: eso lo mide
 * `surfaceAlpha`, y aquí se devuelve el color que sí se ve.
 */
function mixedColor(value: string, theme?: PageTheme): Rgb | null {
  const inner = value.match(/^color-mix\(\s*in\s+[a-z-]+\s*,([\s\S]*)\)$/i)?.[1]
  if (!inner) return null
  /* Las dos partes, por la coma de fuera: dentro puede haber `var(…)` o `rgb(…)`. */
  const parts: string[] = []
  let depth = 0
  let start = 0
  for (let at = 0; at < inner.length; at += 1) {
    const char = inner[at]
    if (char === '(') depth += 1
    else if (char === ')') depth -= 1
    else if (char === ',' && depth === 0) { parts.push(inner.slice(start, at)); start = at + 1 }
  }
  parts.push(inner.slice(start))
  if (parts.length !== 2) return null
  const read = (part: string) => {
    const percent = part.match(/\s([\d.]+)%\s*$/)
    return { color: (percent ? part.slice(0, percent.index) : part).trim(), weight: percent ? Number.parseFloat(percent[1]!) : null }
  }
  const [a, b] = parts.map(read) as [{ color: string; weight: number | null }, { color: string; weight: number | null }]
  if (/^transparent$/i.test(a.color)) return resolvedSurfaceColor(b.color, theme)
  if (/^transparent$/i.test(b.color)) return resolvedSurfaceColor(a.color, theme)
  const first = resolvedSurfaceColor(a.color, theme)
  const second = resolvedSurfaceColor(b.color, theme)
  if (!first || !second) return first ?? second
  const wa = a.weight ?? (b.weight !== null ? 100 - b.weight : 50)
  const wb = b.weight ?? (a.weight !== null ? 100 - a.weight : 50)
  const total = wa + wb || 1
  return first.map((channel, index) => Math.round((channel * wa + second[index]! * wb) / total)) as Rgb
}

/** Cuánto cubre un solo color, de 0 (no se ve) a 1 (tapa lo de detrás). */
function colorAlpha(token: string, theme: PageTheme | undefined, depth: number): number {
  const value = token.trim()
  if (/^transparent$/i.test(value)) return 0
  if (value.startsWith('var(')) {
    const resolved = resolveVar(value, theme)
    return resolved === value || depth > 3 ? 1 : surfaceAlpha(resolved, theme, depth + 1)
  }
  const hex = value.match(/^#([0-9a-f]{4}|[0-9a-f]{8})$/i)?.[1]
  if (hex) return Number.parseInt(hex.length === 4 ? hex[3]!.repeat(2) : hex.slice(6, 8), 16) / 255
  const functional = value.match(/^(?:rgba?|hsla?)\(([^)]*)\)$/i)?.[1]
  if (functional) {
    const alpha = functional.split(/[\s,/]+/).filter(Boolean)[3]
    if (!alpha) return 1
    return alpha.endsWith('%') ? Number.parseFloat(alpha) / 100 : Number.parseFloat(alpha)
  }
  if (/^color-mix\(/i.test(value) && /transparent/i.test(value)) {
    /* `color-mix(in srgb, X 20%, transparent)`: cubre lo que pesa X. Con el
       porcentaje puesto a «transparent», lo que le falta. */
    const onTransparent = value.match(/transparent\s+([\d.]+)%/i)
    if (onTransparent) return 1 - Number.parseFloat(onTransparent[1]!) / 100
    const onColor = value.match(/([\d.]+)%/)
    return onColor ? Number.parseFloat(onColor[1]!) / 100 : 0.5
  }
  return 1
}

/**
 * Cuánto tapa un fondo lo que tiene detrás, de 0 a 1: la media de sus colores
 * en un degradado, 0 si es transparente.
 *
 * Hace falta porque `resolvedSurfaceColor` sólo mira el tono: un velo
 * `rgba(255,255,255,0.1)` sobre una sección oscura se leía «claro» y cortaba el
 * blanco de la sección —el texto se quedaba oscuro sobre oscuro—, y un tinte
 * `color-mix(… var(--wb-text) 5%, transparent)` se leía «oscuro» y ponía el
 * texto blanco sobre un fondo claro. Lo que casi no se ve no decide.
 */
export function surfaceAlpha(value: string | undefined, theme?: PageTheme, depth = 0): number {
  if (!value || /^\s*none\s*$/i.test(value)) return 0
  const trimmed = value.trim()
  const single = /^(transparent|#[0-9a-f]{3,8}|(?:rgba?|hsla?)\([^)]*\)|var\(--[\w-]+\))$/i.test(trimmed) || /^color-mix\(/i.test(trimmed)
  if (single) return colorAlpha(trimmed, theme, depth)
  const stops = trimmed.match(/color-mix\((?:[^()]|\([^()]*\))*\)|#[0-9a-fA-F]{3,8}\b|(?:rgba?|hsla?)\([^)]*\)|var\(--[\w-]+\)|\btransparent\b/gi) ?? []
  if (!stops.length) return 1
  return stops.reduce((sum, stop) => sum + colorAlpha(stop, theme, depth), 0) / stops.length
}

/** Lo mínimo que tiene que tapar un fondo para decidir el color del texto encima. */
export const OPAQUE_ENOUGH = 0.5

export function relativeLuminance([red, green, blue]: Rgb): number {
  const channels = [red, green, blue].map((channel) => {
    const normalized = channel / 255
    return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!
}

/**
 * Si una superficie es oscura.
 *
 * 0.179 es donde el blanco y el negro tienen el mismo contraste según WCAG.
 * Elegir por ese umbral maximiza el contraste en vez de fiarse del nombre del
 * color.
 */
export function isDarkSurface(color: Rgb): boolean {
  return relativeLuminance(color) < 0.179
}

/**
 * Si la letra de un BOTÓN relleno de este color va en blanco.
 *
 * Más generoso con el blanco que `isDarkSurface`: sobre un azul, un verde o un
 * rojo de marca de tono medio el negro gana por poco en contraste medido, pero
 * un botón así con letra negra se lee como un error. Blanco mientras llegue a
 * 3:1 —el mínimo de WCAG para componentes y letra de botón—; oscuro sólo en
 * colores claros de verdad: amarillo, lima, pasteles, gris claro.
 * (Contraste con blanco = 1.05 / (L + 0.05) ≥ 3 ⇔ L ≤ 0.30.)
 */
export const BUTTON_LIGHT_TEXT_MAX_LUMINANCE = 0.3
export function buttonWantsLightText(color: Rgb): boolean {
  return relativeLuminance(color) <= BUTTON_LIGHT_TEXT_MAX_LUMINANCE
}

/**
 * El fondo de una pieza con superficie propia —una tarjeta—, o `null` si deja
 * ver lo de detrás.
 *
 * Distinto de `resolvedSurfaceColor` en una cosa: lo transparente no se adivina.
 * La tarjeta de fábrica es `color-mix(in srgb, var(--wb-card) 0%, transparent)`,
 * y promediar sus colores diría «blanca» de algo que no pinta nada: encima va
 * la letra de la página, no la de una tarjeta que no se ve.
 */
export function solidSurfaceColor(value: string | undefined, theme?: PageTheme): Rgb | null {
  if (!value || /transparent/i.test(value)) return null
  return resolvedSurfaceColor(value, theme)
}

/** La letra automática encima de un color del tema, por su variable: blanca si
 *  es oscuro, `null` —la de siempre— si no o si no se puede saber. */
export function inkOnColorVar(cssVar: string, theme: PageTheme): string | null {
  const color = resolvedSurfaceColor(`var(${cssVar})`, theme)
  return color && isDarkSurface(color) ? ON_DARK_TEXT : null
}

/**
 * La letra del sitio como VALOR y no como variable.
 *
 * Hace falta en dos casos: una superficie clara dentro de una oscura —donde
 * `--wb-text` ya se cambió a blanco más arriba— y un fondo elegido que ES la
 * letra, que apuntando a `var(--wb-text)` se volvería blanco con ella.
 */
export function siteTextColor(theme: PageTheme): string {
  const value = typeof theme.colors?.text === 'string' ? theme.colors.text.replace(/[<>{};\\]/g, '').trim() : ''
  return value || '#111827'
}
